import * as geometryEngine from "@arcgis/core/geometry/geometryEngine.js";
import Polygon from "@arcgis/core/geometry/Polygon";
import Graphic from "@arcgis/core/Graphic";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import WFSLayer from "@arcgis/core/layers/WFSLayer";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import axios, { AxiosResponse } from "axios";
import { convertGeometrySpatialRefs } from "../../lib/CordinatesConversion/convertGeometrySpatialRefs";
import { getSymbols, roofObsType, treeSymbolObj } from "../../lib/symbols/getSymbols";
import { baseURL, requestUrl } from "../baseUrls";
import { MINUSEABLEAREA, parentBuildingIndex } from "../Const";
import globalLayers, { building_index } from "../CustomerMaps/Maps/GlobaLMap";
import { defaultBody } from "./defaultPanelsConfig";
import { buildingBase } from "./fetch3d";
import { featureLyarGeo, geoServerObject } from "./geoservertypes";
import { WFSURL, WFSURLParentId } from "./getWFSUrls";


export enum RoofFactor {
    RCC = 0.7,
    METAL = 0.9,
    HYBRID= 1
  }

  type options = {
    sanctionload? : number[];
    indextoUpdate? : number;
}

type attributes = { elevation: number, mmsHeight: number, name: string, row: number, tileNumber: number}


export const fetchAllGeometryfromWMS = (url: string, layername: string) => {
    const wmsLayer = new WFSLayer({
        url: url,
        name: `pvbuilding:${layername}`
    })
    return wmsLayer;
}

// This WFS layer expects latitude, longitude in the CQL polygon coordinates.
const buildingLookupPolygon = (latitude: number, longitude: number, graphicLayer?: string | null): string | null => {
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180 || (lat === 0 && lng === 0)) {
        return null;
    }

    if (graphicLayer) {
        try {
            const savedGraphic = JSON.parse(graphicLayer);
            const geometry = savedGraphic?.geometry;
            const ring = geometry?.rings?.[0] as number[][] | undefined;
            const wkid = geometry?.spatialReference?.wkid;
            if (ring && ring.length >= 3 && (wkid === 4326 || wkid === 3857 || wkid === 102100 || wkid === 102113)) {
                const coordinates = ring.map(([x, y]) => {
                    if (wkid === 4326) return [x, y];
                    const roofLng = x * 180 / 20037508.342789244;
                    const roofLat = (2 * Math.atan(Math.exp(y * Math.PI / 20037508.342789244)) - Math.PI / 2) * 180 / Math.PI;
                    return [roofLng, roofLat];
                });
                if (coordinates.every(([x, y]) => Number.isFinite(x) && Number.isFinite(y) && Math.abs(x) <= 180 && Math.abs(y) <= 90)) {
                    const lngValues = coordinates.map(([x]) => x);
                    const latValues = coordinates.map(([, y]) => y);
                    const centerLng = (Math.min(...lngValues) + Math.max(...lngValues)) / 2;
                    const centerLat = (Math.min(...latValues) + Math.max(...latValues)) / 2;
                    if (Math.abs(centerLat - lat) > 0.01 || Math.abs(centerLng - lng) > 0.01) throw new Error("Saved roof is outside the selected location");
                    const [firstLng, firstLat] = coordinates[0];
                    const [lastLng, lastLat] = coordinates[coordinates.length - 1];
                    if (firstLng !== lastLng || firstLat !== lastLat) coordinates.push(coordinates[0]);
                    return `POLYGON((${coordinates.map(([roofLng, roofLat]) => `${roofLat} ${roofLng}`).join(", ")}))`;
                }
            }
        } catch (error) {
            // Older projects may have no usable saved roof geometry.
        }
    }

    // A roughly 50 m square covers the selected building when no boundary was saved.
    const latDelta = 25 / 111320;
    const lngDelta = 25 / (111320 * Math.cos(lat * Math.PI / 180));
    const minLng = lng - lngDelta;
    const maxLng = lng + lngDelta;
    const minLat = lat - latDelta;
    const maxLat = lat + latDelta;
    return `POLYGON((${minLat} ${minLng}, ${minLat} ${maxLng}, ${maxLat} ${maxLng}, ${maxLat} ${minLng}, ${minLat} ${minLng}))`;
};

// partner EPC
export const fetchBuildingfromCoordinates = async (latitude: number, longitude: number, graphicLayer?: string | null): Promise<null | Polygon> => {

    let PolygonBuilding = null as null | Polygon
    const typeNames = globalLayers.wmsLayers.baselayer;
    // const typeNames = globalLayers.wmsLayers.buildingname;
    // const typeNames = globalLayers.wmsLayers.buildingname_old;
    const lookupPolygon = buildingLookupPolygon(latitude, longitude, graphicLayer);
    if (!lookupPolygon) return null;
    const cqlFilter = (type: string) => `INTERSECTS(${type}, ${lookupPolygon})`
    // const wfs = WFSURL(typeNames, cqlFilter(globalLayers.wmsLayers.type_old))
    const wfs = WFSURL(typeNames, cqlFilter(globalLayers.wmsLayers.type))
    var response = null as AxiosResponse<geoServerObject, any> | null;
    try {
        response = await axios.get(wfs)
    } catch (e) {
        console.log(e)
        return null;
    }

    if (!response) return null;

    if (response.status !== 200) {
        console.log("encountered some error while fetching the layer");
        return null;
    }


    let selectedRoof = null as any
    if (response.data?.features.length) {

        globalLayers.selectedRoof.objectid = response?.data.features[0].id.split('.')[1];
        // globalLayers.selectedRoof.objectid = (response?.data.features[0].properties as any).id 
        // globalLayers.selectedRoof.objectid = (response?.data.features[0].properties as any).Id 

        globalLayers.selectedRoof.properties = response?.data.features[0]?.properties
        // globalLayers.elevationP = response?.data.features[0]?.properties.ElevationP;
        const geometry = response?.data.features[0]?.geometry;
        // const [Polygon, GeoJSONLayer] = await loadEsriModules(['esri/geometry/Polygon', 'esri/layers/GeoJSONLayer'])
        if (geometry.coordinates && geometry.coordinates.length) {
            PolygonBuilding = new Polygon({
                rings: geometry.coordinates[0]
            })
            const blob = new Blob([JSON.stringify(response?.data)], {
                type: "application/json"
            });
            const url = URL.createObjectURL(blob);
            let layer = new GeoJSONLayer({
                url,
                title: "GeoJson_Layer"
            }) as __esri.GeoJSONLayer;
            globalLayers.map?.allLayers.forEach((ele: any) => {
                if (ele?.title === layer.title) {
                    globalLayers.map?.remove(ele);
                }

            })

        }
    }

    return PolygonBuilding
}


export const hideFetched_3d  = () => {
    
}

// for fetching all the building then roof from the base layer 
export const fetchChildWMSLayersByParentId_byBaseLayer = async (id: string, { sanctionload, indextoUpdate }: options) => {
    // Show a persistent loading toast
    // const loadingToast = toast.loading("Loading 3D Data, please wait...");
    // store.dispatch(setModalHeaderFooter({title:"Loading 3D Data",secondaryBtnTxt:"",btnTxt:""}))
    // hide lsb-drawer
    // store.dispatch(setIslsbDrawer(false));
    // store.dispatch(setLoading3dData('start'))
    let allGraphic = [] as Graphic[]

    // first call all the building on the base laye id 
    const buildingLayerName = globalLayers.wmsLayers.buildingname;
    const buildingWfs = WFSURLParentId(buildingLayerName, id);// for new roofs 
    try {
        const buildingResponse: AxiosResponse<geoServerObject, any> = await axios.get(buildingWfs);
        const buildingFeature: featureLyarGeo[] = buildingResponse?.data.features as featureLyarGeo[];

        // let buildIndex = 0;
        let indx = -1
        // let skipLoop = false
        // iterate over building and the call for their obs and infra for each roof
         // Use for…of loop to sequentially process each building feature
        for (let i = 0; i < buildingFeature.length; i++) {
            const buildFeature = buildingFeature[i];
             const roofId = buildFeature?.id.split('.')[1];
             const roofLayerName = globalLayers.wmsLayers.roofLayers[0];
             
             // const roofWfsUrl = WFSURLParentId_(roofLayerName, id);
             const roofWfsUrl = WFSURLParentId(roofLayerName, roofId);// for new roofs 
             try {
                 const roofResponse: AxiosResponse<geoServerObject, any> = await axios.get(roofWfsUrl);
                 const roofFeatures: featureLyarGeo[] = roofResponse?.data.features as featureLyarGeo[];
                 // Iterate over each roof feature
                 let indexin = 0
                 for (const roofFeature of roofFeatures) {
                    // skipLoop = false
                    let firstIndex = false
                    
         
                    // Process the roof graphic
                    const roofPolygon_ = new Polygon({
                         rings: roofFeature.geometry.coordinates
                    });
                    const roofPolygonringss =
                        roofPolygon_.rings.map(points => {
                            return points.map(point => [point[0], point[1], 0]);
                        }) 
               
                    const roofPolygon = new Polygon({
                         rings: roofPolygonringss
                    })
     
               
         
                     const convertedRoofGeom = await convertGeometrySpatialRefs(roofPolygon, 102100);
                     const totalarea = geometryEngine.geodesicArea(roofPolygon, 'square-meters')
                     if (!convertedRoofGeom){
                        // skipLoop = true
                         continue;
                     } 
                     if(totalarea < MINUSEABLEAREA) {
                        // skipLoop = true
                        continue;
                     }

                     if(roofLayerName.toLowerCase().includes('rcc')){
                        firstIndex = indexin === 0
                    }
                    // check of graphic is there on this index 
                    // const graphicFound = GetGraphicByNameIndex(buildingBase, indexin)
                    // if(graphicFound){
                    //     firstIndex = true
                    //     indextoUpdate = indexin
                    // }

                     // let roofId = roofFeature.properties.Id;
                    let roofId = (roofFeature).id.split('.')[1]; // for the new layer
                    const roofHeight = parseFloat((roofFeature.properties as any).height) || 0;

                    // skipLoop = false

                    indx += 1
                    indexin  = indexin + 1
                    let factor = RoofFactor.HYBRID
                    if(roofFeature.id.includes('roof')){
                        factor = RoofFactor.HYBRID
                    } // for the New 

                    let graphicName = `${buildingBase + "_" + Math.random() * 100}`
                    globalLayers.lastAddedGraphicName = graphicName
                     const roofGraphic = new Graphic({
                         geometry: convertedRoofGeom,
                         attributes: {
                             height: roofHeight,
                             title:graphicName,
                             name:graphicName,
                             id: roofId,
                             elevation: 0,
                             totalarea: totalarea,
                             useablearea: totalarea * 1
                         }
                     });
                    //  globalLayers.lastAddedGraphicName = graphicName
                    roofGraphic.setAttribute(building_index, indx)
                    roofGraphic.symbol = getSymbols(roofObsType.Building, roofHeight).withHeight //getSymbols(roofObsType.Building).withoutHeight;
                     
                    allGraphic.push(roofGraphic)
                    
                    let finalGraphicGeometry = roofGraphic.geometry;
                     
                    for (const name of globalLayers.wmsLayers.layerNames_new) {
                    // for (const name of globalLayers.wmsLayers.layerNames_old) {
                        if (name === roofLayerName) continue; // Skip roof layer since it's already processed
                        
                        // Use the current roofId for non-roof layers
                        const wfs = WFSURLParentId(name, roofId); // for new layers 
                        // const wfs = WFSURLParentId_(name, roofId);
                        try {
                            const response: AxiosResponse<geoServerObject, any> = await axios.get(wfs);
                            const features = response?.data.features as featureLyarGeo[];
                            let Keepouts = [] as Graphic[]
                          
                            for (const item of features) {
                                // Create the polygon geometry for the graphic
                            
                                // UNCOMMENT THIS FOR THE NEW lAYERS 
                                const childWMSPolygon_ = new Polygon({
                                    rings: item.geometry.coordinates
                                });
                
                                const rings =
                                childWMSPolygon_.rings.map(points => {
                                        return points.map(point => [point[0], point[1], roofHeight]);
                                    })
                        
                                const childWMSPolygon = new Polygon({
                                    rings: rings
                                })
    
                                //  // for old 
                                // const childWMSPolygon = new Polygon({
                                //     rings: (item.geometry.coordinates[0] as number[][][]).map(points => {
                                //         return points.map(point => [point[0], point[1], 0]);
                                //     })
                                // });
        
                                // Convert the coordinate system
                                const convertedGeom = await convertGeometrySpatialRefs(childWMSPolygon, 102100);

                                // subract this geometry form roofgraphic 
                                finalGraphicGeometry = geometryEngine.difference(finalGraphicGeometry, convertedGeom as Polygon) as Polygon
                                const Obstotalarea = geometryEngine.geodesicArea(childWMSPolygon, 'square-meters')
                                if (!convertedGeom) continue;
        
                                // Create the graphic and set the appropriate symbol
                                // const height = (item.properties as any).Height ? parseFloat((item.properties as any).Height) : 0;
                                const infraHeight = (item.properties as any).height ? parseFloat((item.properties as any).height) : 0;
                                const title = name.split(':')[1] ?? name
                                
                                const graphic = new Graphic({
                                    geometry: convertedGeom,
                                    attributes: {
                                        height: isNaN(infraHeight) ? 0 : infraHeight,
                                        title: `${title + "_" + Math.random() * 100}`,
                                        name: `${title + "_" + Math.random() * 100}`,
                                        id: item.id.split('.')[1],
                                        elevation: roofHeight,
                                        parentBuildingIndex: indx,
                                        useablearea: Obstotalarea,
                                        infraheight: infraHeight,
                                        infraoffset: infraHeight,
                                        offset: infraHeight,
                                        parentRoofName: graphicName
                                    }
                                });
                                graphic.setAttribute(parentBuildingIndex, indx)
                                // Apply the appropriate symbology based on the layer type
                                if (name.toLowerCase().includes(roofObsType['ExistingSolarModules'])) {
                                    graphic.symbol = getSymbols(roofObsType.ExistingSolarModules, infraHeight).withHeight
                                    // graphic.symbol = solarPaneExistingSymbologywithHeight(height);
                                } else if (name.toLowerCase().includes(roofObsType['WaterTanks'])) {
                                    graphic.symbol = getSymbols(roofObsType.WaterTanks, infraHeight).withHeight
                                    // graphic.symbol = InfraSymbologyHeight(height);
                                } else if (name.toLowerCase().includes(roofObsType['MumtyStructure'])) {
                                    graphic.symbol = getSymbols(roofObsType.MumtyStructure, infraHeight).withHeight
                                    // graphic.symbol = mumtyStructureSymbologywithHeight(height);
                                } else if (name.toLowerCase().includes(roofObsType['HvacEquipment'])) {
                                    graphic.symbol = getSymbols(roofObsType.HvacEquipment, infraHeight).withHeight
                                    // graphic.symbol = hvacSymbologywithHeight(height);
                                } else if (name.toLowerCase().includes(roofObsType['Pipelines'])) {
                                    graphic.symbol = getSymbols(roofObsType.Pipelines, infraHeight).withHeight
                                    // graphic.symbol = pipelineSymbologywithHeight(height);
                                } else if (name.toLowerCase().includes(roofObsType['ParapetWall'])) { 
                                    graphic.symbol = getSymbols(roofObsType.ParapetWall, infraHeight).withHeight
                                    // globalLayers.parapetwallArr[indx] = infraHeight;
                                    // let data = store.getState().EPCDetails.roofAnalysis.roofDetails[indx].structureDetails;
                                
                                    // store.dispatch(setstructureDetailsbyIndex({ index: indx, ...data, parapetwall: infraHeight.toString() }))
                                    // graphic.symbol = papapetSymbologyHeight(height);
                                } else if(name.toLowerCase().includes(roofObsType['TreeLine'])){
                                    graphic.symbol = treeSymbolObj().withHeight as any
                                }  else if(name.toLowerCase().includes(roofObsType['Chimneys'])){
                                    graphic.symbol = getSymbols(roofObsType.Chimneys, infraHeight).withHeight
                                }
                                else if(name.toLowerCase().includes(roofObsType['Helipad'])){
                                    graphic.symbol = getSymbols(roofObsType.Helipad, infraHeight).withHeight
                                }
                                else if(name.toLowerCase().includes(roofObsType['Skylights'])){
                                    graphic.symbol = getSymbols(roofObsType.Skylights, infraHeight).withHeight
                                }
                                else if(name.toLowerCase().includes(roofObsType['Mobile'])){
                                    graphic.symbol = getSymbols(roofObsType.Mobile, infraHeight).withHeight
                                }
                                else if(name.toLowerCase().includes(roofObsType['ExtendedColumns'])){
                                    graphic.symbol = getSymbols(roofObsType.ExtendedColumns, infraHeight).withHeight
                                }
                                else {
                                    graphic.symbol = getSymbols(roofObsType.Handrail, infraHeight).withHeight
                                }

                                // const roofDetailsIndex = store.getState().EPCDetails.roofAnalysis.roofDetailsIndx
                                //  const keepoutModuleLayout = store.getState().EPCDetails.roofAnalysis.roofDetails[roofDetailsIndex].keepoutsDetails[indx].keepoutModuleLayout
                                
    
                                if(!name.toLowerCase().includes('parapet')){
                                    const data = {
                                        data: {
                                        keepoutModuleLayout:{} as any,
                                            infraheight:infraHeight,
                                            infraoffset:infraHeight,
                                            infraType:  name.split(':')[1].replaceAll('_', ' '),
                                            uid: (graphic as any).uid,
                                            totalarea: Obstotalarea,
                                            name: graphic.getAttribute("name"),
                                            graphic: JSON.stringify(graphic),
                                            ischecked: false,
                                            keepoutTypes:"Others" as any
                                        }, 
                                        index:indx 
                                    }
                                    // store.dispatch(setInfraDesignDatabyIndex(data))
                                    Keepouts.push(graphic)
                                    allGraphic.push(graphic)
                                } 
                                // else  store.dispatch(setParapetwallbyIndex({ data: infraHeight, index: indx }))
                            }

                            // fetch panel layers for this roof  finalGraphicGeometry 
                            // // call api for generation grid graphics 
                            const panlesResponse: AxiosResponse<{ attributes: attributes, rings: number[][][] }[], any> = await baseURL.post(requestUrl.getgraphicingrid, defaultBody)
                            if(panlesResponse.statusText !== 'OK'){
                                console.log(response.status, response.data)
                            }
                            // store these panles somewhere
                        
                        } catch (error) {
                            console.error(`Error fetching data for layer ${name}:`, error);
                        }
                    }
         
                 }
         
                 if(globalLayers.sketchVM.activeTool){
                     globalLayers.sketchVM.cancel()
                 }
                 
             } catch (error) {
                 console.error(`Error fetching roof layer data:`, error);
             }
             
        }
      
        // for (let index = 0; index < globalLayers.totalNumberofModulesArr.length; index++) {
        //     const selectedProject = store.getState().EPCDetails.roofAnalysis.selectedProject
        //     const noofModules = globalLayers.totalNumberofModulesArr[index];
        //     const totalUseableRoofAreaArr = globalLayers.totalUseableRoofAreaArr[index];
        //     const totalarea = globalLayers.totalroofAreaArr[index];
        //     const maxPlantCapacity = globalLayers.maxPlantCapacity[index];
        //     const parapetwallArr = globalLayers.parapetwallArr[index] ?? 0;
        //     const defaultmodulePower = globalLayers.defaultmodulePowerArr[index];
        //     const height = globalLayers.buildingHeight[index];
        //     let santionlaod = maxPlantCapacity;
        //     if(index === 0 && selectedProject.length && selectedProject[0].installationmode){
        //         santionlaod =  typeof selectedProject[index].sanctionload === 'string' ? parseInt(selectedProject[index].sanctionload as any) : selectedProject[index].sanctionload as number
        //     }

        //     store.dispatch(setModuleInfobyIndex({
        //         totalModules: noofModules,
        //         maxplantcapacity: maxPlantCapacity,
        //         buildingheight: height?parseFloat(height.toFixed(2)):0,
        //         index: index,
        //         sanctionload: santionlaod
        //     }))
        //     store.dispatch(setRoofDesingDetails({
        //         data: {
        //             useablearea: totalUseableRoofAreaArr,
        //             totalarea,
        //             totalModules: noofModules.toString(),
        //             buildingheight: height.toFixed(2),
        //             defaultmodulePower: defaultmodulePower.toString(),
        //             parapetheight: parapetwallArr.toString(),
        //             parapetoffset: '0',
        //             plantDCpower: maxPlantCapacity.toString()
        //         }, index: index
        //     }))

        //     if(index === 0){
        //         store.dispatch(setSelectedProjectDetails({
        //                 useablearea: totalUseableRoofAreaArr,
        //                 totalroofarea:totalarea,
        //                 noofmodules: noofModules,
        //                 buildingheight: toFixed(height),
        //                 parapetwall: parapetwallArr,
        //                 maxplantcapacity: maxPlantCapacity.toString(),
        //                 graphicName: store.getState().EPCDetails.roofAnalysis.roofDetails[index].graphicName,
        //                 // keepoutsDetails
        //         }))   
        //     }
        // }

        return allGraphic
        
    } catch (error) {
        
    } 
    return allGraphic
    // toast.update(loadingToast, {
    //     render: "Fetching Completed!",
    //     type: "success",
    //     isLoading: false,
    //     autoClose: 3000,
    //   });

    
    // store.dispatch(setLoading3dData('stop'))

    // const firstRoof =GetGraphicByNameIndex(buildingBase, 0);
    // if(firstRoof){
    //     // flyToGeometry((firstRoof.geometry as Polygon))
    // }

    // // makeRoofVisibilityByIndex(0, true)
    // globalLayers.toggleRoofAccordian = 'Roof 1'
    // // highlight the selected graphic boundry 
    // globalLayers.fetched3d = false
};

